<template>
  <div class="pin-login-step card-base fade-in">
    <div class="card-body p-4">
      <h4 class="card-title mb-4 text-uppercase">
        <span class="gradient-text">Enter</span> <span class="text-primary-color">PIN</span>
      </h4>

      <div class="container mb-2">
        <div class="row d-flex justify-content-center">
          <div class="input-animation">
            <label class="pin-input-label">
              <input 
                class="input-amount" 
                ref="passwordField" 
                @keyup.enter="handleLogin" 
                placeholder="Enter your PIN"
                :type="passwordFieldType" 
                v-model="password"
              >
            </label>

            <div class="show-password-container">
              <label class="show-password-checkbox">
                <input type="checkbox" v-model="showPassword">
                <span class="checkbox-text">Show PIN</span>
              </label>
            </div>

            <div class="forgot-pin text-end mt-2">
              <a href="#" class="forgot-link" @click.prevent="resetPassword">Forgot PIN? Reset</a>
            </div>
          </div>

          <div class="container button-pane input-animation">
            <div class="row text-center d-flex flex-column align-items-center">
              <div class="col-12 d-inline-flex justify-content-center">
                <button @click="inputNumber($event)" class="keypad-btn" type="button">1</button>
                <button @click="inputNumber($event)" class="keypad-btn" type="button">2</button>
                <button @click="inputNumber($event)" class="keypad-btn" type="button">3</button>
              </div>
              <div class="col-12 d-inline-flex justify-content-center">
                <button @click="inputNumber($event)" class="keypad-btn" type="button">4</button>
                <button @click="inputNumber($event)" class="keypad-btn" type="button">5</button>
                <button @click="inputNumber($event)" class="keypad-btn" type="button">6</button>
              </div>
              <div class="col-12 d-inline-flex justify-content-center">
                <button @click="inputNumber($event)" class="keypad-btn" type="button">7</button>
                <button @click="inputNumber($event)" class="keypad-btn" type="button">8</button>
                <button @click="inputNumber($event)" class="keypad-btn" type="button">9</button>
              </div>
              <div class="col-12 d-inline-flex justify-content-center">
                <button @click="addPoint" class="keypad-btn" type="button">.</button>
                <button @click="inputNumber($event)" class="keypad-btn" type="button">0</button>
                <button @click="backspace" class="keypad-btn function-btn" type="button">
                  <font-awesome-icon icon="fa-solid fa-delete-left"/>
                </button>
              </div>
            </div>
          </div>

          <div class="text-center my-3 input-animation">
            <button :disabled="loading || !usersReady" @click="handleLogin" type="submit" class="login-button btn-primary-lightning">
              <span class="button-text">{{ !usersReady ? 'Loading...' : 'Login' }}</span>
              <span v-if="loading" class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { responses } from '@/utils/responses';
import { Storage } from '@/services/Storage';
import { authentication as auth } from '@/services/auth';
import axios from 'axios';
import Swal from 'sweetalert2';
import { generateUUID } from '@/utils/uuid';

export default {
  name: 'PinLoginStep',
  data() {
    return {
      password: '',
      loading: false,
      usersReady: false,
      showPassword: false
    };
  },
  computed: {
    passwordFieldType() {
      return this.showPassword ? 'text' : 'password';
    }
  },
  async mounted() {
    // Ensure store is set
    const defaultStore = await Storage.getDefaultStore();
    if (defaultStore === null) {
      this.$emit('error', 'No store selected');
      return;
    }

    // Load users if not already loaded
    this.loading = true;
    try {
      let hasUsers = await auth.hasUsers();
      if (!hasUsers) {
        await this.$store.dispatch('getAllUsers');
      }
    } catch (e) {
      // Fallback: always try to fetch users if check fails
      try {
        await this.$store.dispatch('getAllUsers');
      } catch (_) {
        // Continue even if fetching users fails
      }
    }
    this.usersReady = true;
    this.loading = false;

    // Auto-focus PIN input
    await new Promise(resolve => setTimeout(resolve, 500));
    this.$refs.passwordField?.focus();
  },
  methods: {
    async resetPassword() {
      try {
        const { value: emailInput } = await Swal.fire({
          title: 'Reset PIN',
          input: 'email',
          inputLabel: 'Enter your email address',
          inputPlaceholder: 'name@example.com',
          showCancelButton: true,
          confirmButtonText: 'Send Request',
          inputValidator: (value) => {
            if (!value) return 'Please enter your email address';
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) return 'Please enter a valid email address';
            return undefined;
          }
        });
        if (!emailInput) return;

        const email = String(emailInput).trim();
        const storeDB = await Storage.getCurrentStoreDB();
        if (!storeDB) {
          return responses.throwErr('Store not set up on this device.');
        }

        const localid = generateUUID();
        const url = `${backOfficeTomcatApi}auth/${storeDB}/requestPinReset`;
        
        this.loading = true;
        Swal.showLoading();
        const { status, data } = await axios.post(url, { localid, email });
        
        if (status >= 200 && status < 300) {
          const message = data?.message || 'If the email exists, a reset instruction has been sent.';
          responses.showSuccess('Request Sent');
          await Swal.fire({ icon: 'success', title: 'Request Sent', text: message });
          return;
        }
        throw Object.assign(new Error('Failed to send reset request'), { response: { status, data } });
      } catch (err) {
        try { if (window?.errorHandler?.tomcatError) window.errorHandler.tomcatError(err, true); } catch(_){}
        const status = err?.response?.status;
        const serverMsg = err?.response?.data?.message || err?.response?.data?.error || err?.message;
        let friendly = serverMsg || 'Unable to process reset request at this time.';
        responses.throwErr(friendly);
      } finally {
        Swal.hideLoading?.();
        this.loading = false;
      }
    },

    async handleLogin() {
      this.loading = true;

      try {
        const res = await this.$store.dispatch('posLogin', this.password);

        // if (res.status === 404) {
        //   responses.throwErr('PIN verification server is unavailable. Please try again when the server is back online.');
        //   this.loading = false;
        //   return;
        // }

        if (!res.isValid) {
          responses.throwErr('Incorrect PIN');
          this.loading = false;
          return;
        }

        const isAdmin = res.username === 'Admin';
        await this.$store.dispatch('recordlogin', res.username);

        // Emit success with user info
        this.$emit('next', { 
          step: 'pin-complete', 
          data: { 
            isAdmin,
            username: res.username 
          } 
        });
      } catch (err) {
        responses.throwErr('Login failed. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    inputNumber(event) {
      let elem = event.target;
      if (elem) {
        let txt = elem.textContent || elem.innerText;
        if (txt) this.password += txt;
      }
    },

    addPoint() {
      if (!this.password.includes('.') && this.password !== '0') {
        this.password += '.';
      }
    },

    backspace() {
      let password = this.password;
      let l = password.length;
      this.password = password.substring(0, l - 1);
      l = this.password.length;
      if (l === 0) {
        this.password = '';
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.pin-login-step {
  padding: 0;
  width: 100%;
  background-color: var(--card-bg);
  border: var(--card-border);
  box-shadow: var(--card-shadow);
  border-radius: 16px;
  overflow: hidden;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--card-shadow-hover);
  }
}

.card-title {
  font-weight: 700;
  letter-spacing: 1.2px;
  margin-bottom: 2rem;
  text-align: center;
  font-size: 1.5rem;
}

.text-primary-color {
  color: var(--text-primary);
}

.gradient-text {
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple), var(--accent-electric-blue));
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  opacity: 1;
}

.input-animation {
  opacity: 1;
  transform: translateY(0);
}

.pin-input-label {
  display: block;
  width: 100%;
  margin-bottom: 1.5rem;
}

.input-amount {
  width: 100%;
  display: block;
  margin: 0 auto;
  max-width: 350px;
  height: 56px;
  border-radius: 12px;
  border: var(--card-border);
  background-color: var(--card-bg);
  color: var(--text-primary);
  outline: transparent;
  text-align: center;
  font-size: 24px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.05);

  &:focus {
    border-color: var(--accent-electric-blue);
    box-shadow: 0 0 0 3px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.2);
    transform: translateY(-2px);
  }

  &:hover:not(:focus) {
    border-color: var(--accent-electric-blue);
  }
}

.show-password-container {
  display: flex;
  justify-content: center;
}

.show-password-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  margin-bottom: 20px;
  transition: all 0.3s ease;

  &:hover {
    color: var(--accent-electric-blue);

    .checkbox-text {
      color: var(--accent-electric-blue);
    }
  }
}

.checkbox-text {
  margin-left: 8px;
  color: var(--text-secondary);
  font-size: 0.9rem;
  transition: color 0.3s ease;
}

.forgot-pin {
  margin-bottom: 1rem;
}

.forgot-link {
  color: var(--accent-electric-blue);
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;

  &:hover {
    color: var(--accent-neon-purple);
  }
}

.button-pane {
  margin-top: 1.5rem;
}

.keypad-btn {
  display: inline-block;
  font-weight: 700;
  font-family: var(--font-family);
  font-size: 24px;
  color: var(--text-primary);
  text-align: center;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  background-color: var(--card-bg);
  border: var(--card-border) !important;
  padding: .375rem .75rem;
  line-height: 1.5;
  border-radius: 12px !important;
  transition: all 0.3s ease;
  height: 64px;
  width: 84px;
  margin: 5px;
  box-shadow: var(--card-shadow);

  &:hover {
    transform: translateY(-3px);
    box-shadow: var(--card-shadow-hover);
    background-color: var(--secondary-button-bg);
  }

  &:active {
    transform: translateY(1px);
    background-color: var(--secondary-button-bg-dark);
  }

  &.function-btn {
    color: var(--accent-electric-blue);

    &:hover {
      color: var(--accent-neon-purple);
    }
  }
}

.login-button {
  width: 100%;
  max-width: 350px;
  height: 56px;
  font-size: 1.1rem;
  font-weight: 600;
  letter-spacing: 1.2px;
  border-radius: 12px;
  position: relative;
  overflow: hidden;
  margin: 1rem auto 0;
  background: linear-gradient(to right, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
  border: none;
  box-shadow: 0 4px 12px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.3);
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(var(--accent-electric-blue-rgb, 51, 156, 255), 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg, 
      rgba(255, 255, 255, 0) 0%, 
      rgba(255, 255, 255, 0.3) 50%, 
      rgba(255, 255, 255, 0) 100%
    );
    transition: all 0.8s ease;
  }

  &:hover:not(:disabled)::before {
    left: 100%;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .button-text {
    position: relative;
    z-index: 2;
  }

  .spinner-grow {
    margin-left: 8px;
  }
}

/* Animations removed for compatibility with reduced motion settings */

/* Dark mode adjustments */
[data-theme='dark'] {
  .keypad-btn {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1) !important;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }
  }

  .input-amount {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.1);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .keypad-btn {
    height: 56px;
    width: 72px;
    font-size: 20px;
    margin: 4px;
  }

  .card-body {
    padding: 1.5rem !important;
  }
}
</style>
